import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaWhatsapp, FaEnvelope, FaPhone, FaPaperPlane, FaUser, FaTag } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from 'emailjs-com';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Prepare data for email
      const emailData = {
        name: data.name,
        email: data.email,
        subject: data.subject || 'Contact Form Submission',
        message: data.message,
        contactType: 'Contact Form Submission'
      };
  
      // Send email using EmailJS with public key
      await emailjs.send(
        process.env.REACT_APP_EMAIL_SERVICE_ID,
        process.env.REACT_APP_EMAIL_CONTACT_TEMPLATE_ID, // Use the contact-specific template
        emailData,
        process.env.REACT_APP_EMAIL_PUBLIC_KEY
      );
      
      setSubmitSuccess(true);
      reset();
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-6 md:p-8 h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {submitSuccess ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12 h-full flex flex-col items-center justify-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mb-6">
            <div className="text-green-500 dark:text-green-400 text-5xl">✓</div>
          </div>
          <h3 className="text-2xl font-bold mb-4 text-secondary-900 dark:text-white">Message Sent Successfully!</h3>
          <p className="text-secondary-600 dark:text-secondary-300 mb-8 max-w-md">
            Thank you for reaching out. Our team will review your message and get back to you as soon as possible.
          </p>
          <motion.button
            onClick={() => setSubmitSuccess(false)}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-300 flex items-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPaperPlane className="mr-2" />
            Send Another Message
          </motion.button>
        </motion.div>
      ) : (
        <>
          <h2 className="text-2xl font-bold mb-6 text-secondary-900 dark:text-white flex items-center">
            <FaPaperPlane className="mr-3 text-primary-600 dark:text-primary-400" />
            Get in Touch
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.a 
              href="tel:+919411305200" 
              className="flex flex-col items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <FaPhone className="text-primary-600 dark:text-primary-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-secondary-900 dark:text-white font-medium">Call Us</span>
              <span className="text-secondary-600 dark:text-secondary-400 text-sm">+91 94113 05200</span>
            </motion.a>
            
            <motion.a 
              href="mailto:Vedusscm@gmail.com" 
              className="flex flex-col items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <FaEnvelope className="text-primary-600 dark:text-primary-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-secondary-900 dark:text-white font-medium">Email Us</span>
              <span className="text-secondary-600 dark:text-secondary-400 text-sm">Vedusscm@gmail.com</span>
            </motion.a>
            
            <motion.a 
              href="https://wa.me/919411305200" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex flex-col items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <FaWhatsapp className="text-primary-600 dark:text-primary-400 text-2xl mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-secondary-900 dark:text-white font-medium">WhatsApp</span>
              <span className="text-secondary-600 dark:text-secondary-400 text-sm">Chat with us</span>
            </motion.a>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2 flex items-center" htmlFor="name">
                  <FaUser className="mr-2 text-primary-600 dark:text-primary-400" />
                  Full Name *
                </label>
                <input
                  id="name"
                  type="text"
                  className={`w-full px-4 py-3 rounded-md border ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-secondary-300 dark:border-secondary-600 focus:ring-primary-500'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200`}
                  placeholder="Your Name Here"
                  {...register('name', { required: 'Name is required' })}
                />
                {errors.name && <p className="mt-1 text-red-500 text-sm">{errors.name.message}</p>}
              </div>
              
              <div>
                <label className="block text-secondary-700 dark:text-secondary-300 mb-2 flex items-center" htmlFor="email">
                  <FaEnvelope className="mr-2 text-primary-600 dark:text-primary-400" />
                  Email Address *
                </label>
                <input
                  id="email"
                  type="email"
                  className={`w-full px-4 py-3 rounded-md border ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-secondary-300 dark:border-secondary-600 focus:ring-primary-500'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200`}
                  placeholder="john.doe@example.com"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && <p className="mt-1 text-red-500 text-sm">{errors.email.message}</p>}
              </div>
            </div>
            
            <div>
              <label className="block text-secondary-700 dark:text-secondary-300 mb-2 flex items-center" htmlFor="subject">
                <FaTag className="mr-2 text-primary-600 dark:text-primary-400" />
                Subject *
              </label>
              <input
                id="subject"
                type="text"
                className={`w-full px-4 py-3 rounded-md border ${errors.subject ? 'border-red-500 focus:ring-red-500' : 'border-secondary-300 dark:border-secondary-600 focus:ring-primary-500'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200`}
                placeholder="How can we help you?"
                {...register('subject', { required: 'Subject is required' })}
              />
              {errors.subject && <p className="mt-1 text-red-500 text-sm">{errors.subject.message}</p>}
            </div>
            
            <div>
              <label className="block text-secondary-700 dark:text-secondary-300 mb-2 flex items-center" htmlFor="message">
                <FaPaperPlane className="mr-2 text-primary-600 dark:text-primary-400" />
                Message *
              </label>
              <textarea
                id="message"
                rows="5"
                className={`w-full px-4 py-3 rounded-md border ${errors.message ? 'border-red-500 focus:ring-red-500' : 'border-secondary-300 dark:border-secondary-600 focus:ring-primary-500'} bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:outline-none focus:ring-2 transition-all duration-200`}
                placeholder="Please provide details about your inquiry..."
                {...register('message', { required: 'Message is required' })}
              ></textarea>
              {errors.message && <p className="mt-1 text-red-500 text-sm">{errors.message.message}</p>}
            </div>
            
            <div className="text-center pt-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-8 rounded-md transition-colors duration-300 flex items-center justify-center mx-auto ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="mr-2" />
                    Send Message
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </>
      )}
    </motion.div>
  );
};

export default ContactForm;